import { useState } from 'react'
import { Button } from '../../ui/Button'
import { TextField } from '../../ui/TextField'
import { Logo } from '../../app/Logo'
import { requestMagicLink, startGoogleLogin, startMicrosoftLogin } from './supabase'
import { useAuth } from '../../app/providers/AuthProvider'
import styles from './LoginPage.module.css'

/** The providers' own marks. Inline SVG rather than a hosted image: an external
 *  request on the sign-in screen is a dependency on somebody else's CDN at the
 *  one moment the user cannot proceed without us. */
const GOOGLE_MARK = (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className={styles.providerMark}>
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
  </svg>
)

const MICROSOFT_MARK = (
  <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true" className={styles.providerMark}>
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
)

/** One rule for two jobs: it decides whether the form submits, and whether the
 * button is green. Two copies would drift, and a green button that then
 * refuses the address is worse than a grey one. */
const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export function LoginPage() {
  const { authError } = useAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const canSend = LOOKS_LIKE_EMAIL.test(email.trim())

  async function oauth(start: () => Promise<void>) {
    setError('')
    try {
      await start()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start sign-in.')
    }
  }

  async function magicLink(event: React.FormEvent) {
    event.preventDefault()
    setError('')
    if (!LOOKS_LIKE_EMAIL.test(email.trim())) {
      setError('Enter a valid email address.')
      return
    }
    setBusy(true)
    try {
      await requestMagicLink(email.trim())
      setSent(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the link.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className={styles.page}>
      <div className={styles.card}>
        <Logo />
        <h1 className={styles.title}>Sign in to YourDealPulse</h1>
        <p className={styles.subtitle}>See where every sales conversation is leading.</p>

        <Button className={styles.provider} onClick={() => void oauth(startGoogleLogin)}>
          {GOOGLE_MARK}
          Continue with Google
        </Button>

        <Button className={styles.provider} onClick={() => void oauth(startMicrosoftLogin)}>
          {MICROSOFT_MARK}
          Continue with Microsoft
        </Button>

        <div className={styles.divider}>
          <span>or</span>
        </div>

        <form className={styles.form} onSubmit={magicLink} noValidate>
          <TextField
            label="Email address"
            type="email"
            name="email"
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Button type="submit" disabled={busy} variant={canSend ? 'primary' : 'default'}>
            {busy ? 'Sending…' : 'Email me a sign-in link'}
          </Button>
        </form>

        {/* Announced, not just shown: the old app's messages were invisible to
            assistive technology. */}
        <div aria-live="polite" className={styles.messages}>
          {sent ? <p className={styles.ok}>Check your email for a sign-in link.</p> : null}
          {error || authError ? <p className={styles.error}>{error || authError}</p> : null}
        </div>
      </div>
    </main>
  )
}
